/**
 * Thin client for our API. Cookies carry the session; every change sends the CSRF header the API requires.
 * API addresses are relative ("api/v1/…") and resolve against the page's <base href>: "/" on the app's own
 * domain, "/fsp/" (for example) when a customer's website serves the app through its connected-panel proxy.
 */

import type { Level, PlanId } from '@fsp/core';

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, public readonly fields: { path: string; code: string }[] = []) {
    super(code);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers['x-fsp-request'] = '1';
  if (body !== undefined) headers['content-type'] = 'application/json';
  const response = await fetch(path.replace(/^\//, ''), { method, headers, credentials: 'same-origin', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data as { error?: { code?: string; fields?: { path: string; code: string }[] } } | null)?.error;
    throw new ApiError(response.status, error?.code ?? 'server_error', error?.fields ?? []);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

export interface Me {
  user: { id: number; name: string; email: string; locale: string; mustChangePassword: boolean };
  company: { id: number; name: string; isVatPayer: boolean; role: string; hasLogo?: boolean; kind?: 'company' | 'iv' };
  /** The platform admin looking at this panel (read-only). */
  viewAsAdmin?: boolean;
  /** In a client's company through its accounting firm: the firm (the way back to its board). */
  firm?: { id: number; name: string } | null;
  /** The demo server (made-up companies, reset every hour). */
  demo?: boolean;
  subscription?: Subscription;
  /** Paying by card through Stripe (web only); status is Stripe's subscription status. */
  billing?: { enabled: boolean; status: string | null };
  /** Panel sections switched off in Parduotuvė: left out of the menu. */
  disabledModules?: string[];
  /** Every active company of this person (switching between them). */
  companies?: { id: number; name: string; role: string }[];
  /** The agreement with Arkabi: required (approved text, paid plan) and where it stands. */
  contract?: { required: boolean; status: 'none' | 'issued' | 'uploaded' | 'accepted' | 'returned' };
}

export interface Subscription {
  plan: PlanId; state: 'free' | 'paid' | 'trial' | 'expired' | 'none';
  /** What the plan opens now (Premium in the trial, Nemokamas after it ends unpaid) and the sections it does not. */
  level: Level; locked: string[];
  /** Premium paid by the company's accounting firm. */
  coveredByFirm?: boolean;
  trialEndsAt: string | null; paidUntil: string | null; priceCents: number | null; listPriceCents: number | null; daysLeft: number | null; notice: boolean;
}

export interface Customer {
  id: number;
  name: string;
  companyCode: string | null;
  vatCode: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
  contactPerson: string | null;
  bankName: string | null;
  iban: string | null;
  swift: string | null;
  notes: string | null;
}

export interface AdminCompany {
  id: number;
  name: string;
  code: string | null;
  status: 'pending' | 'active' | 'rejected' | 'suspended' | 'closed';
  createdAt: string;
  ownerName: string | null;
  ownerEmail: string | null;
  lastLoginAt: string | null;
  planNote?: string | null;
  subscription?: Subscription;
  /** B3a: the latest access request to this company (the owner approves by e-mail). */
  viewRequest?: { state: 'pending' | 'approved' | 'declined' | 'used' | 'revoked' | 'expired'; requestedAt: string; approvedUntil: string | null } | null;
}

/** Path of this app under its origin ("" on the app domain, "/fsp" behind a connected panel), from <base href>. */
export const basePath = new URL(document.baseURI).pathname.replace(/\/$/, '');
